import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
// SEC-002: the real admin tier code is NEVER sent to the browser. Admin UI
// gates on a non-secret sentinel so existing truthiness checks keep working;
// all backend authorization is done via the signed session token.
const ADMIN_SENTINEL = "session";

// The real session token now lives ONLY in an httpOnly cookie (JS can't read
// it). Same-origin, so the browser attaches it automatically; withCredentials
// makes that explicit for XHR.
axios.defaults.withCredentials = true;

// ── Global request interceptor ───────────────────────────────────────────────
// Store users still identify via their PIN header; admins/trainees rely on the
// httpOnly session cookie (no token in JS), so no auth header is attached here.
if (!axios.__domiopsAuthInterceptor) {
  axios.__domiopsAuthInterceptor = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = config.url || "";
      if (!url.includes("/api/")) return config;
      const role = localStorage.getItem("authRole");
      const h = config.headers || {};
      if (role === "store") {
        const pin = localStorage.getItem("masterCode");
        if (pin && !h["x-store-code"]) h["x-store-code"] = pin;
      }
      config.headers = h;
    } catch { /* no-op */ }
    return config;
  });
}

/**
 * Auth model:
 *   • Admins (Super Admin + Area Manager) log in with USERNAME + PASSWORD, then
 *     their OWN authenticator (per-user TOTP). On success the server returns a
 *     session token + the tier authorization code (masterCode) which is sent as
 *     X-Master-Code on every request — so tier-based route authorization is
 *     unchanged.
 *   • Stores log in with a fast PIN (no username, no 2FA) — unchanged.
 *
 * pending2FA (admins, between password-correct and TOTP-verified):
 *   { tier, enrolled, preAuthToken, name }
 */
export function AuthProvider({ children }) {
  const [adminLevel, setAdminLevel] = useState(null);   // 'super' | 'area' | null
  const [adminTenant, setAdminTenant] = useState("real"); // 'real' | 'demo'
  const [storeRole, setStoreRole] = useState(null);
  const [masterCode, setMasterCode] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [pending2FA, setPending2FA] = useState(null);
  const [trainingRole, setTrainingRole] = useState(null);
  const [checking, setChecking] = useState(true);

  const clearAuth = useCallback(() => {
    setAdminLevel(null);
    setAdminTenant("real");
    setStoreRole(null);
    setMasterCode(null);
    setSessionToken(null);
    setPending2FA(null);
    setTrainingRole(null);
    localStorage.removeItem("masterCode");
    localStorage.removeItem("authRole");
    localStorage.removeItem("storeCode");
    localStorage.removeItem("adminTier");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("adminTenant");
  }, []);

  // Restore a saved session on load. The session token is in an httpOnly cookie,
  // so restore just re-verifies with the server (cookie sent automatically).
  useEffect(() => {
    const code = localStorage.getItem("masterCode");
    const role = localStorage.getItem("authRole");
    const storeCode = localStorage.getItem("storeCode");

    if (role === "admin") {
      // Optimistically restore admin state from the (non-secret) markers so a
      // flaky/cold-start verify never bounces the user; the httpOnly cookie is
      // the real authority and the call below confirms or (on a definitive 401)
      // clears it.
      const savedTier = localStorage.getItem("adminTier");
      if (savedTier === "super" || savedTier === "area") {
        setAdminLevel(savedTier);
        setAdminTenant(localStorage.getItem("adminTenant") || "real");
        setMasterCode(ADMIN_SENTINEL);
        setSessionToken(ADMIN_SENTINEL);
      }
      axios.post(`${API}/auth/admin/session/verify`)
        .then(r => {
          if (r.data?.valid && (r.data.tier === "super" || r.data.tier === "area")) {
            setAdminLevel(r.data.tier);
            setAdminTenant(r.data.tenant || "real");
            localStorage.setItem("adminTenant", r.data.tenant || "real");
            setMasterCode(ADMIN_SENTINEL);
            setSessionToken(ADMIN_SENTINEL);
          } else { clearAuth(); }
        })
        // Only a definitive 401 means the session is truly gone. A network
        // error / preview cold-start 5xx must NOT log the user out.
        .catch((err) => { if (err?.response?.status === 401) clearAuth(); })
        .finally(() => setChecking(false));
      return;
    }

    if (role === "store" && storeCode && code) {
      axios.post(`${API}/auth/verify`, null, { headers: { "x-master-code": code } })
        .then(r => {
          if (r.data?.valid && r.data?.role === "store" && r.data?.store_code === storeCode) {
            setStoreRole({ code: storeCode });
            setMasterCode(code);
          } else { clearAuth(); }
        })
        .catch((err) => { if (err?.response?.status === 401) clearAuth(); })
        .finally(() => setChecking(false));
      return;
    }

    if (role === "trainee") {
      axios.post(`${API}/auth/trainee/verify`)
        .then(r => { if (r.data?.valid) { setTrainingRole(true); setSessionToken(ADMIN_SENTINEL); } else { clearAuth(); } })
        .catch((err) => { if (err?.response?.status === 401) clearAuth(); })
        .finally(() => setChecking(false));
      return;
    }

    const id = setTimeout(() => setChecking(false), 0);
    return () => clearTimeout(id);
  }, [clearAuth]);

  // Background poll — forces logout within ~30s of a password reset / deactivate
  // / revoke. IMPORTANT: only a DEFINITIVE server response logs the user out —
  // a transient network error or preview cold-start 5xx is ignored so it can't
  // eject an otherwise-valid session (this was the cause of "random" logouts).
  useEffect(() => {
    if (!masterCode) return undefined;
    const tick = async () => {
      try {
        if (adminLevel) {
          await axios.post(`${API}/auth/admin/session/verify`);   // 200 = still valid; 401 → caught below
        } else if (storeRole) {
          const r = await axios.post(`${API}/auth/verify`, null, { headers: { "x-master-code": masterCode } });
          if (!r.data?.valid || r.data?.role !== "store") clearAuth();
        }
      } catch (e) {
        if (e?.response?.status === 401) clearAuth();   // server says session is gone
        // otherwise (network/timeout/5xx): keep the session and retry next tick
      }
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [adminLevel, sessionToken, storeRole, masterCode, clearAuth]);

  // Keep-warm ping (cold-start mitigation).
  useEffect(() => {
    let cancelled = false;
    const ping = () => { if (!cancelled) axios.get(`${API}/health/ping`).catch(() => {}); };
    ping();
    const id = setInterval(ping, 10 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── STORE login (PIN) ──────────────────────────────────────────────────────
  const applyStoreAuth = (store_code, code) => {
    setAdminLevel(null);
    setStoreRole({ code: store_code });
    setMasterCode(code);
    setSessionToken(null);
    setPending2FA(null);
    localStorage.setItem("masterCode", code);
    localStorage.setItem("authRole", "store");
    localStorage.setItem("storeCode", store_code);
    localStorage.removeItem("adminTier");
    localStorage.removeItem("sessionToken");
    localStorage.removeItem("adminTenant");
  };

  const login = async (code) => {
    try {
      const res = await axios.post(`${API}/auth/store/login`, null, { headers: { "x-master-code": code }, withCredentials: true });
      const { valid, role, store_code, requires_device_verification } = res.data;
      if (valid && role === "store") {
        applyStoreAuth(store_code, code);
        return { valid: true, role: "store", store_code };
      }
      if (requires_device_verification) {
        // New/unrecognised device — caller must collect a manager approval code.
        return { valid: false, requiresDeviceVerification: true, store_code, pin: code };
      }
      return { valid: false, error: res.data?.error, locked: !!res.data?.locked, retryAfter: res.data?.retry_after || 0, lockType: res.data?.lock_type };
    } catch (e) {
      const d = e?.response?.data || {};
      const detail = d.detail || d.error;
      return { valid: false, error: typeof detail === "string" ? detail : undefined, locked: !!d.locked, retryAfter: d.retry_after || 0, lockType: d.lock_type };
    }
  };

  // Verify a NEW store device with a manager (Area/Super) 2FA code. On success
  // the backend sets the long-lived HttpOnly trust cookie and we complete login.
  const verifyStoreDevice = async (pin, code, label) => {
    try {
      const res = await axios.post(`${API}/auth/store/verify-device`, { code, label: label || "Store device" },
        { headers: { "x-master-code": pin }, withCredentials: true });
      if (res.data?.valid && res.data?.role === "store") {
        applyStoreAuth(res.data.store_code, pin);
        return { valid: true, store_code: res.data.store_code };
      }
      return { valid: false, error: "Verification failed" };
    } catch (e) {
      const d = e?.response?.data || {};
      return { valid: false, error: typeof d.detail === "string" ? d.detail : "Invalid manager approval code" };
    }
  };

  // ── ADMIN login STEP 1 — username + password ────────────────────────────────
  // Returns the pre-auth data (does NOT advance to 2FA — the caller plays the
  // unlock animation, then calls enterTwoFactor()).
  const adminLogin = async (username, password) => {
    try {
      const res = await axios.post(`${API}/auth/admin/login`, { username, password });
      const { valid, tier, name, enrolled, pre_auth_token } = res.data;
      if (!valid) return { valid: false, error: "Invalid username or password" };
      return { valid: true, tier, name, enrolled, preAuthToken: pre_auth_token };
    } catch (e) {
      const d = e?.response?.data || {};
      const detail = d.detail;
      return { valid: false, error: typeof detail === "string" ? detail : "Invalid username or password", locked: !!d.locked, retryAfter: d.retry_after || 0, lockType: d.lock_type };
    }
  };

  // ── ADMIN login STEP 2 — open the Token ID (2FA) screen ─────────────────────
  const enterTwoFactor = (data) =>
    setPending2FA({ tier: data.tier, enrolled: data.enrolled, preAuthToken: data.preAuthToken, name: data.name });

  const beginEnrollment = async () => {
    if (!pending2FA?.preAuthToken) throw new Error("No pending login");
    const res = await axios.post(`${API}/auth/admin/2fa/begin`, null, {
      headers: { "x-pre-auth-token": pending2FA.preAuthToken },
    });
    return res.data; // {otpauth_uri, qr_png_base64, enrolled}
  };

  // Verify the 6-digit code. Returns the finalize payload but does NOT set auth
  // state — the caller plays the unlock animation, then calls finalizeAdmin().
  const verifyTwoFactor = async (code) => {
    if (!pending2FA?.preAuthToken) return { valid: false, error: "No pending login" };
    const path = pending2FA.enrolled ? "/auth/admin/2fa/verify" : "/auth/admin/2fa/confirm";
    try {
      const res = await axios.post(`${API}${path}`, { code }, {
        headers: { "x-pre-auth-token": pending2FA.preAuthToken },
      });
      const { session_token, tier, name } = res.data;
      if (!session_token && !tier) return { valid: false, error: "Server did not issue a session" };
      // Session is delivered as an httpOnly cookie — we only keep non-secret markers.
      return { valid: true, sessionToken: ADMIN_SENTINEL, tier, masterCode: ADMIN_SENTINEL, name, tenant: res.data.tenant || "real" };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      return { valid: false, error: typeof detail === "string" ? detail : "Invalid code" };
    }
  };

  const finalizeAdmin = (data) => {
    const lvl = data.tier === "super" ? "super" : "area";
    setAdminLevel(lvl);
    setAdminTenant(data.tenant || "real");
    localStorage.setItem("adminTenant", data.tenant || "real");
    setStoreRole(null);
    setMasterCode(ADMIN_SENTINEL);
    setSessionToken(ADMIN_SENTINEL);
    setPending2FA(null);
    localStorage.setItem("masterCode", ADMIN_SENTINEL);
    localStorage.setItem("authRole", "admin");
    localStorage.setItem("adminTier", lvl);
    localStorage.removeItem("sessionToken");   // secret lives in the httpOnly cookie only
    localStorage.removeItem("storeCode");
  };

  // ── TRAINEE login (shared account, no 2FA) ──────────────────────────────────
  const applyTraineeAuth = () => {
    setAdminLevel(null);
    setStoreRole(null);
    setMasterCode(null);
    setPending2FA(null);
    setTrainingRole(true);
    setSessionToken(ADMIN_SENTINEL);
    localStorage.setItem("authRole", "trainee");
    localStorage.removeItem("sessionToken");   // secret lives in the httpOnly cookie only
    localStorage.removeItem("masterCode");
    localStorage.removeItem("storeCode");
    localStorage.removeItem("adminTier");
  };

  // Trainee login step 2 (every login): a Super/Area Admin's own 6-digit code.
  const approveTrainee = async (preAuthToken, code) => {
    try {
      const res = await axios.post(`${API}/auth/trainee/approve`, { code },
        { headers: { "x-pre-auth-token": preAuthToken }, withCredentials: true });
      if (!res.data?.valid) return { valid: false, error: "Approval failed" };
      return { valid: true, apply: applyTraineeAuth };
    } catch (e) {
      const d = e?.response?.data?.detail;
      return { valid: false, error: typeof d === "string" ? d : "Invalid manager approval code", expired: e?.response?.status === 401 };
    }
  };

  // ── UNIFIED login — one username + password, the server decides the account
  // type. Admin → caller continues to enterTwoFactor(); store → logged in here
  // (or device verification required); trainee → logged in here.
  const loginUnified = async (username, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password }, { withCredentials: true });
      const d = res.data || {};
      if (d.kind === "admin") {
        return { kind: "admin", valid: true, tier: d.tier, name: d.name, enrolled: d.enrolled, preAuthToken: d.pre_auth_token };
      }
      if (d.kind === "trainee") {
        return { kind: "trainee", valid: false, requiresAdminApproval: true, preAuthToken: d.pre_auth_token };
      }
      if (d.kind === "store") {
        if (d.valid) { applyStoreAuth(d.store_code, password); return { kind: "store", valid: true, store_code: d.store_code }; }
        if (d.requires_device_verification) return { kind: "store", valid: false, requiresDeviceVerification: true, store_code: d.store_code, pin: password };
      }
      return { valid: false, error: "Invalid username or password" };
    } catch (e) {
      const d = e?.response?.data || {};
      const detail = d.detail;
      return { valid: false, error: typeof detail === "string" ? detail : "Invalid username or password", locked: !!d.locked, retryAfter: d.retry_after || 0, lockType: d.lock_type };
    }
  };

  const cancelTwoFactor = () => setPending2FA(null);
  const logout = () => {
    // Cookie is sent automatically → backend revokes the session (jti) and
    // clears the httpOnly cookie. Best-effort; local state cleared regardless.
    axios.post(`${API}/auth/admin/logout`).catch(() => {});
    clearAuth();
  };
  const adminHeaders = () => ({});   // auth travels via the httpOnly session cookie

  const isAdmin = adminLevel === "super" || adminLevel === "area";
  const isSuperAdmin = adminLevel === "super";
  const isAreaManager = adminLevel === "area";
  const isTrainee = !!trainingRole;
  const isDemoAccount = adminTenant === "demo";

  return (
    <AuthContext.Provider value={{
      isAdmin, isSuperAdmin, isAreaManager, adminLevel, isDemoAccount,
      storeRole, masterCode, sessionToken, checking, pending2FA,
      trainingRole, isTrainee, approveTrainee, loginUnified,
      login, adminLogin, enterTwoFactor, beginEnrollment, verifyTwoFactor,
      finalizeAdmin, cancelTwoFactor, logout, adminHeaders, verifyStoreDevice,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
